import { useMemo } from 'react'
import { useUserSettings, useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import {
  GetCategoryForFilterPageQuery,
  GetCategoryForLandingPageQuery
} from 'src/graphql/generated/graphql-types'
import {
  getCategoryUrl,
  getProductUrl,
  getRelativeProductImageUrl,
} from '@dustin-web/microsite-components/src/utils/urls'
import { cloudflareImageLoader } from '@dustin-web/microsite-components/src/image/cloudflare-loader'
import { flatten, isNotNullOrUndefined } from '@dustin-web/microsite-data/src/utils'

type ProductsColectionStructuredData = {
  '@type': string;
  position: number;
  item: {
    '@type': string;
    name: string;
    url: string;
    category: string;
    image: any;
    AggregateRating?: {
      '@type': string;
      reviewCount: number;
      ratingValue: number;
    };
    offers: {
      '@type': string;
      availability: string;
      price?: number | undefined;
      priceCurrency?: string,
    }
  };
}

const getCommonProperties = (
  page: GetCategoryForFilterPageQuery | GetCategoryForLandingPageQuery,
  cultureCode: string
) => {
  const name = page.category?.seoLongTitleName ?? page.category?.name
  const description = stripHtmlTags(page.category?.seoText || '')
  const inLanguage = cultureCode

  const commonProperties = {
    name,
    description,
    inLanguage,
  }

  return commonProperties
}
const stripHtmlTags = (html:string) => {
  const regex = /(<([^>]+)>)/gi;
  const temp = html.replace(regex, " ");

  return temp || '';
}

type CategoryFilterProps = {
  page: GetCategoryForFilterPageQuery[]
}

export const CategoryFilterSchema = ({ page }: CategoryFilterProps) => {
  const { cultureCode } = useUserSettings()
  const { siteUrl, currencyCode } = useSiteContext()
  const productReferences = page
  .map(d => d.category?.productSearch?.productReferences)
  .filter(isNotNullOrUndefined)

  const products = useMemo(() => flatten(productReferences), [productReferences])
  const categoryFilterPage = (page?.[0] as GetCategoryForFilterPageQuery) ?? []
  const commonProperties = getCommonProperties(categoryFilterPage, cultureCode)
  const data = { ...commonProperties }
  const url = `${siteUrl}${getCategoryUrl(categoryFilterPage.category?.pathSlug)}`

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            ...data,
            url: url,
            mainEntity: {
              '@type': 'ItemList',
              itemListElement:
              (products || [])
                .map((product, index) => {
                  var imageUrl
                  if (product?.searchResultProduct?.primaryImageId) {
                    const imageUrlFromApi = getRelativeProductImageUrl(
                      product?.searchResultProduct?.primaryImageId,
                      500,
                      undefined,
                      ''
                    )

                    const imageSrc = cloudflareImageLoader({ src: imageUrlFromApi, width: 500 })
                    imageUrl = imageSrc
                  }
                  const productUrl = `${siteUrl}${getProductUrl(
                    product?.searchResultProduct?.id || '',
                    product?.searchResultProduct?.nameSlug || ''
                  )}`

                  const productStructuredData = {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@type': 'Product',
                      name:
                        (product?.searchResultProduct?.displayName || '') +
                        (product?.searchResultProduct?.displaySpecifications || ''),
                      url: productUrl,
                      category: product?.searchResultProduct?.categoryName || '',
                      image: imageUrl,
                      offers :{
                        '@type': 'offer',
                        price: product?.searchResultProduct?.price?.price,
                        priceCurrency: currencyCode
                      }
                    } 
                  } as ProductsColectionStructuredData

                  if (product?.searchResultProduct?.reviewCount) {
                    productStructuredData.item.AggregateRating = {
                      '@type': 'AggregateRating',
                      reviewCount: product?.searchResultProduct?.reviewCount,
                      ratingValue: product?.searchResultProduct?.reviewScore / 20,
                    }
                  }

                  return productStructuredData
                }) || [],
            },
          }),
        }}
      />
    </div>
  )
}

type CategoryProps = {
  page: GetCategoryForLandingPageQuery
}

export const CategorySchema = ({ page }: CategoryProps) => {
  const { cultureCode } = useUserSettings()
  const { siteUrl, currencyCode } = useSiteContext()
  const commonProperties = getCommonProperties(page, cultureCode)
  const data = { ...commonProperties }
  const url = `${siteUrl}${getCategoryUrl(page.category?.pathSlug)}`

  let allProducts = [
    ...(page?.category?.productLists?.bestsellers?.productReferences || []),
    ...(page?.category?.productLists?.newProducts?.productReferences || []),
    ...(page?.category?.productLists?.campaignProducts?.productReferences || []),
    ...(page?.category?.productLists?.popularAccessories?.productReferences || []),
    ...((page?.category?.productLists?.ads || []).flatMap(ad => ad.productReferences) || []),
  ]

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            ...data,
            url: url,
            mainEntity: {
              '@type': 'ItemList',
              itemListElement:
              (allProducts || [])
                .map((product, index) => {
                  var imageUrl
                  if (product?.searchResultProduct?.primaryImageId) {
                    const imageUrlFromApi = getRelativeProductImageUrl(
                      product?.searchResultProduct?.primaryImageId,
                      500,
                      undefined,
                      ''
                    )

                    const imageSrc = cloudflareImageLoader({ src: imageUrlFromApi, width: 500 })
                    imageUrl = imageSrc
                  }
                  const productUrl = `${siteUrl}${getProductUrl(
                    product?.searchResultProduct?.id || '',
                    product?.searchResultProduct?.nameSlug || ''
                  )}`

                  const productStructuredData = {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@type': 'Product',
                      name:
                        (product?.searchResultProduct?.displayName || '') +
                        (product?.searchResultProduct?.displaySpecifications || ''),
                      url: productUrl,
                      category: product?.searchResultProduct?.categoryName || '',
                      image: imageUrl,
                      offers :{
                        '@type': 'offer',
                        price: product?.searchResultProduct?.price?.price,
                        priceCurrency: currencyCode
                      }
                    },
                  } as ProductsColectionStructuredData
                  
                  if (product?.searchResultProduct?.reviewCount) {
                    productStructuredData.item.AggregateRating = {
                      '@type': 'AggregateRating',
                      reviewCount: product?.searchResultProduct?.reviewCount,
                      ratingValue: product?.searchResultProduct?.reviewScore / 20,
                    }
                  }

                  return productStructuredData
                }) || [],
            },
          }),
        }}
      />
    </div>
  )
}
